import { useAppDispatch, useAppSelector } from 'store/hooks';
import React, { useEffect, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import EmptyImage from 'static/select-module-guide.png';
import { updateCurrentModuleProperties } from 'store/content/contentSlice';
import { Column, Row } from 'ui/Flex';
import { ContentModule } from '../models/ContentModule';
import { ModuleForm } from './ModuleForm';

interface ModulesConfigurationProps {
    className?: string;
    handleSubmit: () => void;
    onDelete: (id: string) => void;
}

const getContent = ({
    module,
    selectedModuleId,
    updateCurrentModuleProperties,
    handleSubmit,
    onDelete
}: {
    module: ContentModule;
    selectedModuleId: string;
    updateCurrentModuleProperties: (fieldName: string, value: any, id: string) => void;
    handleSubmit: () => void;
    onDelete: (id: string) => void;
}) => {
    if (!!module) {
        return (
            <ModuleForm
                updateCurrentModuleProperties={updateCurrentModuleProperties}
                module={module}
                onDelete={onDelete}
                moduleId={selectedModuleId}
                handleSubmit={handleSubmit}
            />
        );
    }
    return (
        <Column align="center" valign="center" style={{ padding: '16px', minWidth: '500px' }}>
            <Row align="center">
                <img src={EmptyImage} width="50%" height="50%" alt="" />
            </Row>
            <Row style={{ marginTop: '16px' }} align="center">
                <Typography variant="caption">Click a module to begin editing</Typography>
            </Row>
        </Column>
    );
};

export const ModulesConfiguration: React.FC<ModulesConfigurationProps> = ({
    handleSubmit,
    onDelete,
    className
}) => {
    const currentModules = useAppSelector(state => state.content.currentModules);
    const selectedModuleId = useAppSelector(state => state.content.selectedModuleId);
    const dispatch = useAppDispatch();

    const updateModuleProperties = React.useCallback(
        (fieldName: string, value: any, id: string) => {
            dispatch(updateCurrentModuleProperties({ fieldName, value, id }));
        },
        [dispatch]
    );

    const [content, setContent] = useState(null);
    const currentModule = React.useMemo(
        () =>
            !!currentModules && selectedModuleId
                ? currentModules.find(item => item.id === selectedModuleId)
                : null,
        [currentModules, selectedModuleId]
    );

    useEffect(() => {
        setContent(
            getContent({
                handleSubmit,
                selectedModuleId,
                updateCurrentModuleProperties: updateModuleProperties,
                module: currentModule,
                onDelete
            })
        );
    }, [selectedModuleId, currentModule, handleSubmit, updateModuleProperties, onDelete]);

    return <Paper className={className}>{content}</Paper>;
};
